import type { Id, ISODate } from '@contracts/common';
import type { Priority, TaskListFlags, TaskRecord } from '@contracts/tasks';
import { addDays, parseDate } from './dates';

/**
 * How far ahead “sắp đến hạn” reaches, in calendar days past the business day (spec §5.1).
 *
 * The single source of this number. The dashboard reaches it through `listFlags` below, the keyset list
 * through the SQL predicate in `apps/api/src/modules/tasks/pagination.ts`, and the worker's reminder scan
 * through `apps/worker/src/scheduler.ts`; all three import it from here, because a copy that drifts makes
 * `/api/dashboard` and `/api/workplace/items?section=soon` answer different work to the same reader on the same day.
 */
export const SOON_DAYS = 3;

/** Urgent first, low last (spec §5.1). */
const rank: Record<Priority, number> = { urgent: 0, high: 1, normal: 2, low: 3 };

/**
 * The BOD who receives this task's notifications and whom the `Của tôi` filter matches: its recipient
 * (spec §5.2, §10).
 */
export function recipientUser(task: TaskRecord): Id {
  return task.recipient.userId;
}

/**
 * Membership of the four Điều hành lists on business day `d`. Deleted and completed tasks are in no list;
 * one task may be in several. A task due on `d` is not overdue yet, and `soon` spans `d` … `d + SOON_DAYS`
 * calendar days inclusive, so it also contains what is due today.
 */
export function listFlags(t: TaskRecord, d: ISODate): TaskListFlags {
  const day = parseDate(d);
  const open = t.deletedAt === null && t.status !== 'completed';
  return {
    today: open && t.deadline <= day,
    blocked: open && t.blocked,
    soon: open && day <= t.deadline && t.deadline <= addDays(day, SOON_DAYS),
    important: open && (t.priority === 'high' || t.priority === 'urgent'),
  };
}

/**
 * Whether a task is overdue on business day `d`: still open — neither deleted nor completed — and due
 * strictly before `d`. A task due on `d` itself is not overdue yet, exactly as in `listFlags` and in the
 * worker's reminder scan. `d` is always the server's day; a browser never supplies its own.
 */
export function isOverdue(t: Pick<TaskRecord, 'deadline' | 'status' | 'deletedAt'>, d: ISODate): boolean {
  return t.deletedAt === null && t.status !== 'completed' && t.deadline < parseDate(d);
}

/** Order group inside every list: overdue+blocked, overdue, blocked, then the rest (spec §5.1). */
const group = (t: TaskRecord, d: ISODate): number => (t.deadline < d ? (t.blocked ? 0 : 1) : (t.blocked ? 2 : 3));

/**
 * Total order of the Điều hành lists: group, then urgent→low, then the earlier deadline, then the earlier
 * creation time, then the ID. The last key makes it deterministic, so the same rows always come back in the
 * same order in API, worker and UI.
 */
export function compareTasks(a: TaskRecord, b: TaskRecord, d: ISODate): number {
  return group(a, d) - group(b, d) || rank[a.priority] - rank[b.priority]
    || a.deadline.localeCompare(b.deadline) || a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id);
}
